/*
 * Algolia InstantSearch engine for the collection takeover.
 *
 * Reads `window.algoliaConfig` (populated server-side by `algolia-config.liquid`)
 * and `window.AlgoliaTheme` (helpers + collection context), then mounts modern
 * InstantSearch.js v4 widgets into the containers rendered by
 * `sections/algolia-search.liquid`. Globals come from the vendored UMD builds:
 * `algoliasearch`, `instantsearch`.
 */
(function () {
  'use strict';

  var cfg = window.algoliaConfig;
  var theme = window.AlgoliaTheme || {};
  var collection = theme.collection;

  var root = document.querySelector('[data-algolia-search]');
  if (!root) return;

  if (!cfg || !cfg.app_id || !cfg.search_api_key || !cfg.index_prefix) {
    // Misconfigured store metafields — fail loud in the console, not silently.
    console.warn('[algolia] missing app_id / search_api_key / index_prefix in shop metafields');
    return;
  }
  // algoliasearch v5 lite ships as a UMD namespace on window['algoliasearch/lite']
  // exposing liteClient(), not the v4 global algoliasearch() function.
  var algoliaLite = window['algoliasearch/lite'];
  if (typeof instantsearch === 'undefined' || !algoliaLite || typeof algoliaLite.liteClient !== 'function') {
    console.warn('[algolia] vendored libraries failed to load');
    return;
  }

  var w = instantsearch.widgets;
  var baseIndex = cfg.index_prefix + 'products';
  var searchClient = algoliaLite.liteClient(cfg.app_id, cfg.search_api_key);
  var distinct = Boolean(cfg.show_products);

  // ---- Collection + stock scope ----------------------------------------
  var filters = [];
  if (collection) {
    filters.push(
      cfg.collection_id_indexing
        ? 'collection_ids:"' + collection.id + '"'
        : 'collections:"' + collection.handle + '"'
    );
  }
  if (cfg.stock_policy === 'deny') {
    filters.push('inventory_quantity > 0');
  } else if (cfg.stock_policy === 'continue') {
    filters.push('inventory_available:true');
  }

  // ---- Sort orders -----------------------------------------------------
  // Shape: [{ key, asc:{active,title}, desc:{active,title} }]; replica index is
  // `<baseIndex>_<key>_<asc|desc>`. First entry is always relevance (base index).
  function isActive(side) {
    return side && (side.active === true || side.active === '1');
  }
  var sortItems = [{ label: 'Relevance', value: baseIndex }];
  (cfg.sort_orders || []).forEach(function (so) {
    if (isActive(so.asc)) {
      sortItems.push({ label: so.asc.title, value: baseIndex + '_' + so.key + '_asc' });
    }
    if (isActive(so.desc)) {
      sortItems.push({ label: so.desc.title, value: baseIndex + '_' + so.key + '_desc' });
    }
  });

  // ---- Facets ----------------------------------------------------------
  // Shape: [{ enabled, name, title, type, searchable }]. `hidden`/disabled are
  // dropped; the rest map to refinementList / menu / rangeInput widgets.
  var facets = (cfg.facets || []).filter(function (f) {
    var enabled = f.enabled === true || parseInt(f.enabled, 10) === 1;
    return enabled && f.type !== 'hidden';
  });

  function prettyRange(value) {
    // price_range buckets arrive as "min:max" major-unit strings.
    return String(value)
      .split(':')
      .map(function (n) {
        return theme.money(Number(n)).replace(/\.\d+/, '');
      })
      .join('-');
  }

  // The price-range facet is a refinementList whose buckets are "min:max"
  // strings; it's identified by attribute name (`price_range`), not by a
  // dedicated `type` — matching how `algolia-search.css` targets it. Detect by
  // name so label formatting and numeric sort actually fire.
  function isPriceRange(facet) {
    return facet.type === 'price_range' || facet.name === 'price_range';
  }

  // A panel renders the facet title and collapses the whole section when the
  // current result set has no values for that facet.
  function panelFor(facet) {
    return w.panel({
      templates: { header: function () { return facet.title; } },
      hidden: function (options) {
        return !options.items || options.items.length === 0;
      }
    });
  }

  function facetWidget(facet, container) {
    var common = { container: container, attribute: facet.name };
    var Panel = panelFor(facet);

    if (facet.type === 'slider') {
      return Panel(w.rangeInput)(common);
    }
    if (facet.type === 'menu') {
      return Panel(w.menu)(Object.assign({ limit: 10, showMore: true }, common));
    }

    // conjunctive | disjunctive | price_range -> refinementList
    var params = Object.assign(
      {
        operator: facet.type === 'conjunctive' ? 'and' : 'or',
        limit: 12,
        showMore: true,
        showMoreLimit: 100,
        sortBy: ['isRefined', 'name:asc']
      },
      common
    );
    if (isPriceRange(facet)) {
      // Buckets arrive as "min:max" major-unit strings. Format the label at the
      // data layer (not the rendered DOM) so InstantSearch owns the text it
      // paints, and sort numerically by min so $10-$25 doesn't fall after $100.
      params.transformItems = function (items) {
        return items.map(function (item) {
          // refinementList paints `highlighted` (not `label`), and it's
          // captured from the raw value before transformItems runs — so both
          // must be overridden or the "$0-$10" text never shows.
          var label = prettyRange(item.value);
          return Object.assign({}, item, { label: label, highlighted: label });
        });
      };
      // Fixed numeric order by bucket min so the list never reshuffles when a
      // box is checked (no isRefined-first priority).
      params.sortBy = function (a, b) {
        return Number(String(a.name).split(':')[0]) - Number(String(b.name).split(':')[0]);
      };
    }
    if (facet.searchable) {
      params.searchable = true;
      params.searchablePlaceholder = 'Search ' + facet.title;
      params.templates = {
        searchableSubmit: function () {
          return '<svg viewBox="0 0 16 16" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" aria-hidden="true"><circle cx="6.75" cy="6.75" r="5"></circle><line x1="10.5" y1="10.5" x2="15" y2="15"></line></svg>';
        }
      };
    }
    return Panel(w.refinementList)(params);
  }

  // Render a container per facet inside the facets sidebar.
  var facetsRoot = root.querySelector('[data-algolia-facets]');
  var widgets = [];
  facets.forEach(function (facet) {
    var section = document.createElement('div');
    section.className = 'algolia-facet';
    section.setAttribute('data-facet-name', facet.name);
    facetsRoot.appendChild(section);
    widgets.push(facetWidget(facet, section));
  });

  // ---- Routing --------------------------------------------------------
  // Collection pages: default routing (filter state preserved in URL).
  // Search page: custom router that keeps Shopify's ?q= URL format so
  // the header search form and /search links stay consistent.
  var routing = true;
  if (!collection && instantsearch.routers && instantsearch.routers.history) {
    routing = {
      router: instantsearch.routers.history({
        createURL: function (opts) {
          var url = new URL(opts.location.href);
          if (opts.routeState && opts.routeState.query) {
            url.searchParams.set('q', opts.routeState.query);
          } else {
            url.searchParams.delete('q');
          }
          return url.toString();
        },
        parseURL: function (opts) {
          return { query: new URLSearchParams(opts.location.search).get('q') || '' };
        }
      }),
      stateMapping: {
        stateToRoute: function (uiState) {
          var idx = uiState[baseIndex] || {};
          return { query: idx.query || '' };
        },
        routeToState: function (routeState) {
          var s = {};
          s[baseIndex] = { query: (routeState && routeState.query) || '' };
          return s;
        }
      }
    };
  }

  // ---- Core search -----------------------------------------------------
  var search = instantsearch({
    indexName: baseIndex,
    searchClient: searchClient,
    routing: routing,
    future: { preserveSharedStateOnUnmount: true }
  });

  var searchboxEl = root.querySelector('[data-algolia-searchbox]');

  search.addWidgets([
    w.configure({
      hitsPerPage: cfg.collections_full_results_hits_per_page || cfg.products_full_results_hits_per_page || 24,
      distinct: distinct,
      facetingAfterDistinct: distinct,
      maxValuesPerFacet: 100,
      // Surface the queryID on each hit so `algolia-insights.js` can attribute
      // clicks/conversions. No-op for ranking when insights is disabled.
      clickAnalytics: Boolean(cfg.analytics_enabled),
      filters: filters.join(' AND '),
      ruleContexts: collection ? [String(cfg.collection_id_query_rules ? collection.id : collection.handle)] : []
    }),

    w.searchBox({
      container: searchboxEl,
      placeholder: searchboxEl.dataset.placeholder || ('Search ' + (collection ? collection.title : 'products')),
      showReset: true,
      showSubmit: true,
      showLoadingIndicator: true,
      templates: {
        submit: function () {
          return '<svg viewBox="0 0 16 16" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" aria-hidden="true"><circle cx="6.75" cy="6.75" r="5"></circle><line x1="10.5" y1="10.5" x2="15" y2="15"></line></svg>';
        }
      }
    }),

    w.stats({
      container: root.querySelector('[data-algolia-stats]'),
      templates: {
        text: function (data) {
          return data.nbHits.toLocaleString() + ' ' + (data.nbHits === 1 ? 'result' : 'results');
        }
      }
    }),

    w.currentRefinements({
      container: root.querySelector('[data-algolia-current-refinements]'),
      transformItems: function (items) {
        var labelMap = {};
        var priceFacets = {};
        facets.forEach(function (f) {
          labelMap[f.name] = f.title;
          if (isPriceRange(f)) { priceFacets[f.name] = true; }
        });
        // The default widget groups every value of an attribute into one row.
        // Split each refinement into its own chip (one row per selection) and
        // format price buckets like "0:10" as "$0-$10".
        var out = [];
        items.forEach(function (item) {
          var title = labelMap[item.attribute] || item.label;
          (item.refinements || []).forEach(function (refinement) {
            var r = priceFacets[item.attribute]
              ? Object.assign({}, refinement, { label: prettyRange(refinement.label) })
              : refinement;
            out.push(Object.assign({}, item, { label: title, refinements: [r] }));
          });
        });
        return out;
      }
    }),

    w.clearRefinements({
      container: root.querySelector('[data-algolia-clear-refinements]'),
      templates: { resetLabel: 'Clear all' }
    }),

    w.hits({
      container: root.querySelector('[data-algolia-hits]'),
      templates: {
        item: function (hit) {
          return theme.productHitHTML(hit);
        },
        empty: function (data) {
          return '<div class="algolia-empty">No results for <q>' + theme.escapeHtml(data.query) + '</q></div>';
        }
      }
    }),

    w.pagination({ container: root.querySelector('[data-algolia-pagination]') })
  ]);

  if (sortItems.length > 1) {
    search.addWidgets([
      w.sortBy({ container: root.querySelector('[data-algolia-sort]'), items: sortItems })
    ]);
  }

  if (widgets.length) {
    search.addWidgets(widgets);
  }

  search.start();

  // ---- Grid / list layout toggle --------------------------------------
  var hitsEl = root.querySelector('[data-algolia-hits]');
  root.querySelectorAll('[data-algolia-layout]').forEach(function (btn) {
    btn.addEventListener('click', function () {
      var mode = btn.getAttribute('data-algolia-layout');
      hitsEl.classList.toggle('algolia-hits--list', mode === 'list');
      root.querySelectorAll('[data-algolia-layout]').forEach(function (b) {
        b.classList.toggle('is-active', b === btn);
      });
    });
  });

  // ---- Variant colour swatches ----------------------------------------
  // Each hit is one representative variant, so the full colour set is fetched
  // per product (filtered by id, distinct off) and cached. All placeholders on
  // a render are batched into a single multi-query to limit round-trips.
  var variantCache = {};
  var variantInFlight = {};

  function variantsQuery(id) {
    return {
      indexName: baseIndex,
      params: {
        filters: 'id=' + id,
        distinct: false,
        hitsPerPage: 80,
        attributesToRetrieve: ['option1', 'option2', 'option3', 'image', 'product_image', 'handle', 'objectID']
      }
    };
  }

  function renderSwatches(el) {
    var variants = variantCache[el.getAttribute('data-product-id')];
    if (!variants) return;
    var colorIndex = parseInt(el.getAttribute('data-color-index'), 10) || 0;
    var seen = {};
    var items = [];
    variants.forEach(function (v) {
      var color = [v.option1, v.option2, v.option3][colorIndex];
      var img = v.image || v.product_image;
      if (!color || !img || seen[color]) return;
      seen[color] = true;
      items.push({ color: color, image: img, handle: v.handle, variant: v.objectID });
    });
    if (items.length < 2) { el.innerHTML = ''; return; }

    var max = 6;
    var html = items.slice(0, max).map(function (s) {
      var thumb = s.image + (s.image.indexOf('?') === -1 ? '?' : '&') + 'width=80';
      return (
        '<button type="button" class="algolia-swatch" title="' + theme.escapeHtml(s.color) + '"' +
          ' data-image="' + theme.escapeHtml(s.image) + '"' +
          ' data-handle="' + theme.escapeHtml(s.handle) + '"' +
          ' data-variant="' + theme.escapeHtml(String(s.variant)) + '"' +
          ' style="background-image:url(' + theme.escapeHtml(thumb) + ')"></button>'
      );
    });
    if (items.length > max) {
      html.push('<span class="algolia-swatch__more">+' + (items.length - max) + '</span>');
    }
    el.innerHTML = html.join('');
  }

  function loadSwatches() {
    var els = Array.prototype.slice.call(hitsEl.querySelectorAll('[data-algolia-swatches]'));
    if (!els.length) return;
    var need = {};
    els.forEach(function (el) {
      var id = el.getAttribute('data-product-id');
      if (variantCache[id]) renderSwatches(el);
      else if (!variantInFlight[id]) need[id] = true;
    });
    var ids = Object.keys(need);
    if (!ids.length) return;
    ids.forEach(function (id) { variantInFlight[id] = true; });
    searchClient.search(ids.map(variantsQuery)).then(function (response) {
      (response.results || []).forEach(function (r, i) {
        variantCache[ids[i]] = r.hits || [];
      });
      ids.forEach(function (id) { delete variantInFlight[id]; });
      Array.prototype.slice
        .call(hitsEl.querySelectorAll('[data-algolia-swatches]'))
        .forEach(function (el) {
          if (variantCache[el.getAttribute('data-product-id')]) renderSwatches(el);
        });
    }).catch(function () {
      ids.forEach(function (id) { delete variantInFlight[id]; });
    });
  }

  search.on('render', loadSwatches);

  // Clicking a swatch previews that colour in-card and points the links at it.
  hitsEl.addEventListener('click', function (e) {
    var btn = e.target.closest ? e.target.closest('.algolia-swatch') : null;
    if (!btn) return;
    e.preventDefault();
    var card = btn.closest('.product-card');
    if (!card) return;
    var img = card.querySelector('.product-card__image--main img');
    if (img) {
      var src = btn.getAttribute('data-image');
      img.src = src + (src.indexOf('?') === -1 ? '?' : '&') + 'width=450';
    }
    var handle = btn.getAttribute('data-handle');
    var variant = btn.getAttribute('data-variant');
    card.querySelectorAll('a[href^="/products/"]').forEach(function (a) {
      a.href = '/products/' + encodeURIComponent(handle) + '?variant=' + encodeURIComponent(variant);
    });
    card.querySelectorAll('.algolia-swatch').forEach(function (s) {
      s.classList.toggle('is-active', s === btn);
    });
  });

  // ---- Mobile filter drawer -------------------------------------------
  var DRAWER_OPEN_CLASS = 'algolia-filters-open';
  function setDrawer(open) {
    root.classList.toggle(DRAWER_OPEN_CLASS, open);
    // Lock background scroll while the drawer is open so only the panel scrolls.
    document.body.style.overflow = open ? 'hidden' : '';
  }
  var toggle = root.querySelector('[data-algolia-filter-toggle]');
  if (toggle) {
    toggle.addEventListener('click', function () {
      setDrawer(!root.classList.contains(DRAWER_OPEN_CLASS));
    });
  }
  // Header close, footer apply and the backdrop all dismiss the drawer.
  var closeEls = root.querySelectorAll('[data-algolia-filter-close]');
  for (var c = 0; c < closeEls.length; c++) {
    closeEls[c].addEventListener('click', function () {
      setDrawer(false);
    });
  }
  document.addEventListener('keydown', function (e) {
    if (e.key === 'Escape' && root.classList.contains(DRAWER_OPEN_CLASS)) {
      setDrawer(false);
    }
  });
})();
